/**
 * @fileoverview Field modifiers — presentation-only enhancements applied to a
 * field chip's resolved value at render time.
 *
 * A field chip stores the bare field `key`; modifiers let the author choose how
 * the resolved value is *presented* without typing punctuation by hand:
 *   - dateformat: reformat an ISO date (`16/04/2026`, `16 Apr 2026`, …) — only
 *                 meaningful on date fields                  (mutually exclusive)
 *   - wrap:  parens `(v)`, brackets `[v]`, quotes `"v"`     (mutually exclusive)
 *   - case:  upper, lower, capitalize (Title Case)          (mutually exclusive)
 *   - transform: plural, possessive                         (independent toggles)
 *   - layout: stack (render `\n`-separated values one per line)  (mutually exclusive)
 *
 * The `stack` layout modifier is a no-op at the text level — its `apply` is the
 * identity. The actual newline → `<br>` conversion happens in the substitution
 * layer (after HTML escaping), since modifiers run on plain text before
 * escaping and a `<br>` injected here would be escaped to `&lt;br&gt;`.
 *
 * Modifiers persist inside the stored token as a pipe suffix
 * (`{{company_status|parens|plural}}`) and on the chip as
 * `data-field-mods="parens|plural"`. They are applied entirely client-side at
 * substitution time — there is no server-side {{}} substitution.
 */

export type FieldModifier =
  | "parens"
  | "brackets"
  | "quotes"
  | "plural"
  | "possessive"
  | "upper"
  | "lower"
  | "capitalize"
  | "date_dmy_abbr"
  | "date_dmy_slash"
  | "date_dmy_dash"
  | "date_dmy_dot"
  | "date_iso"
  | "date_dmy_ordinal"
  | "date_weekday"
  | "abn_label"
  | "acn_label"
  | "stack";

/** Wrap modifiers — at most one per chip. */
export const WRAP_MODIFIERS = ["parens", "brackets", "quotes"] as const;
/**
 * Layout modifiers — at most one per chip. `stack` renders a `\n`-separated
 * value (e.g. the company name-line fields) with one entry per line.
 */
export const LAYOUT_MODIFIERS = ["stack"] as const;
/** Case modifiers — at most one per chip. */
export const CASE_MODIFIERS = ["upper", "lower", "capitalize"] as const;
/**
 * Prefix (label) modifiers — at most one per chip. They prepend a fixed
 * identifier label (`ABN `, `ACN `) to the resolved value. Because modifiers
 * only ever run on a non-empty value, the label is emitted *only when the
 * field actually resolves* — so e.g. a trust ABN reads `ABN 80 607 131 564`
 * when present, and a company with no trust prints nothing at all (no errant
 * bare "ABN" label). Authors would otherwise have to type "ABN " before the
 * chip, which then shows on every letter regardless of the value.
 */
export const PREFIX_MODIFIERS = ["abn_label", "acn_label"] as const;
/** Grammatical transforms — independently combinable. */
export const TRANSFORM_MODIFIERS = ["plural", "possessive"] as const;
/**
 * Date-format modifiers — at most one per chip. Each reformats a stored date
 * value (ISO `YYYY-MM-DD` or AU day-first, see {@link toIsoDate}); values that
 * aren't dates pass through untouched. The absence of any date-format modifier
 * means the default long form (`16 April 2026`), applied by the substitution
 * layer for date fields.
 */
export const DATEFORMAT_MODIFIERS = [
  "date_dmy_abbr",
  "date_dmy_slash",
  "date_dmy_dash",
  "date_dmy_dot",
  "date_iso",
  "date_dmy_ordinal",
  "date_weekday",
] as const;

export const ALL_MODIFIERS: FieldModifier[] = [
  ...DATEFORMAT_MODIFIERS,
  ...TRANSFORM_MODIFIERS,
  ...CASE_MODIFIERS,
  ...WRAP_MODIFIERS,
  ...PREFIX_MODIFIERS,
  ...LAYOUT_MODIFIERS,
];

type ModifierGroup =
  "dateformat" | "wrap" | "case" | "transform" | "prefix" | "layout";

interface ModifierDef {
  /** Human label for the chip-editing UI. */
  label: string;
  group: ModifierGroup;
  /** Pure transform on a non-empty plain-text value. */
  apply: (v: string) => string;
}

function pluralize(v: string): string {
  if (/[^aeiou]y$/i.test(v)) return v.slice(0, -1) + "ies";
  if (/(s|x|z|ch|sh)$/i.test(v)) return v + "es";
  return v + "s";
}

function possessive(v: string): string {
  return /s$/i.test(v) ? v + "'" : v + "'s";
}

function titleCase(v: string): string {
  return v.replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

interface IsoDate {
  y: number;
  /** 1-12 */
  m: number;
  /** 1-31 */
  d: number;
}

function toIsoParts(y: number, m: number, d: number): IsoDate | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/**
 * Parse a date value into its parts, accepting ISO `YYYY-MM-DD` and AU
 * day-first forms (`17/08/2026`, `17-8-2026`, `17.08.2026`). Returns null for
 * anything else.
 *
 * Canonical storage is ISO, but day-first values reach the formatters too: the
 * generate wizard shows date inputs as DD/MM/YYYY, so a retyped date is stored
 * in that shape and would otherwise skip every date formatter and print raw.
 */
function parseDateValue(v: string): IsoDate | null {
  const trimmed = v.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) return toIsoParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const dayFirst = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(trimmed);
  if (dayFirst) {
    return toIsoParts(
      Number(dayFirst[3]),
      Number(dayFirst[2]),
      Number(dayFirst[1]),
    );
  }
  return null;
}

/**
 * Normalise a date value to canonical ISO `YYYY-MM-DD`, or null when it isn't
 * a recognisable date. Used by the date inputs so what gets stored is always
 * ISO regardless of the format the practitioner typed.
 */
export function toIsoDate(v: string): string | null {
  const parts = parseDateValue(v);
  if (!parts) return null;
  return `${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}`;
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** English ordinal suffix: 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd". */
function ordinalDay(d: number): string {
  const mod100 = d % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${d}th`;
  switch (d % 10) {
    case 1:
      return `${d}st`;
    case 2:
      return `${d}nd`;
    case 3:
      return `${d}rd`;
    default:
      return `${d}th`;
  }
}

function weekdayName({ y, m, d }: IsoDate): string {
  return WEEKDAYS_LONG[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

/**
 * Default long date form (`16 April 2026`) used when a date field carries no
 * date-format modifier. Exported so the substitution layer renders the default
 * through the same code path as the modifier variants.
 */
export function formatDateLong(v: string): string {
  const iso = parseDateValue(v);
  if (!iso) return v;
  return `${iso.d} ${MONTHS_LONG[iso.m - 1]} ${iso.y}`;
}

/** Build a date-format ModifierDef whose `apply` no-ops on unparseable values. */
function dateFormat(label: string, fmt: (iso: IsoDate) => string): ModifierDef {
  return {
    label,
    group: "dateformat",
    apply: (v) => {
      const iso = parseDateValue(v);
      return iso ? fmt(iso) : v;
    },
  };
}

export const MODIFIER_CATALOG: Record<FieldModifier, ModifierDef> = {
  parens: { label: "Parentheses", group: "wrap", apply: (v) => `(${v})` },
  brackets: { label: "Brackets", group: "wrap", apply: (v) => `[${v}]` },
  quotes: { label: "Quotes", group: "wrap", apply: (v) => `"${v}"` },
  plural: { label: "Pluralize", group: "transform", apply: pluralize },
  possessive: { label: "Possessive", group: "transform", apply: possessive },
  upper: { label: "UPPERCASE", group: "case", apply: (v) => v.toUpperCase() },
  lower: { label: "lowercase", group: "case", apply: (v) => v.toLowerCase() },
  capitalize: { label: "Title Case", group: "case", apply: titleCase },
  date_dmy_abbr: dateFormat(
    "D Mon YYYY",
    (i) => `${i.d} ${MONTHS_SHORT[i.m - 1]} ${i.y}`,
  ),
  date_dmy_slash: dateFormat(
    "DD/MM/YYYY",
    (i) => `${pad2(i.d)}/${pad2(i.m)}/${i.y}`,
  ),
  date_dmy_dash: dateFormat(
    "DD-MM-YYYY",
    (i) => `${pad2(i.d)}-${pad2(i.m)}-${i.y}`,
  ),
  date_dmy_dot: dateFormat(
    "DD.MM.YYYY",
    (i) => `${pad2(i.d)}.${pad2(i.m)}.${i.y}`,
  ),
  date_iso: dateFormat("YYYY-MM-DD", (i) => `${i.y}-${pad2(i.m)}-${pad2(i.d)}`),
  date_dmy_ordinal: dateFormat(
    "Do Month YYYY",
    (i) => `${ordinalDay(i.d)} ${MONTHS_LONG[i.m - 1]} ${i.y}`,
  ),
  date_weekday: dateFormat(
    "Weekday, D Month YYYY",
    (i) => `${weekdayName(i)}, ${i.d} ${MONTHS_LONG[i.m - 1]} ${i.y}`,
  ),
  // Prefix labels — prepend an identifier label to a non-empty value so the
  // label only ever prints when the field resolves (see PREFIX_MODIFIERS).
  abn_label: {
    label: 'Prefix "ABN "',
    group: "prefix",
    apply: (v) => `ABN ${v}`,
  },
  acn_label: {
    label: 'Prefix "ACN "',
    group: "prefix",
    apply: (v) => `ACN ${v}`,
  },
  // Layout — identity at the text level. The substitution layer converts the
  // value's newlines to `<br>` (after escaping) when this modifier is present.
  stack: {
    label: "Stack on separate lines",
    group: "layout",
    apply: (v) => v,
  },
};

function isFieldModifier(value: string): value is FieldModifier {
  return Object.prototype.hasOwnProperty.call(MODIFIER_CATALOG, value);
}

/**
 * Connector phrases our company-name concatenation emits
 * (`buildConcatenatedCompanyName`). Used by the `stack` fallback below to break
 * a legacy space-joined value onto separate lines without a re-select.
 */
const COMPANY_NAME_STACK_CONNECTORS =
  /\s+(trading as |also known as |as trustee for )/gi;

/** Upper-case the first letter of a line, leaving the rest untouched. */
function capitalizeLineStart(line: string): string {
  return line.replace(
    /^(\s*)([a-z])/,
    (_, ws: string, ch: string) => ws + ch.toUpperCase(),
  );
}

/**
 * Render an already-HTML-escaped value with the `stack` layout (one entry per
 * line). The primary mechanism is newline → `<br>`: the company name-line
 * fields and newly-built "name with variants" values carry `\n`.
 *
 * Fallback: a legacy "Registered name with variants" value persisted before
 * MOR-625 is joined with spaces, not newlines. So when there are no newlines
 * AND this is the `company_name` field, break before the standard connector
 * phrases instead. Scoped via `key` so no other field is ever reflowed, and the
 * connectors are phrases our own code generates — they don't occur inside real
 * registered/trading names.
 *
 * Each resulting line is sentence-cased (first letter upper-cased) so a split
 * connector reads "Trading as …" / "Also known as …" while the inline (un-
 * stacked) value keeps its lower-case "… trading as …" form untouched.
 */
export function applyStackLayout(escapedValue: string, key?: string): string {
  const lines: string[] | null = /\r?\n/.test(escapedValue)
    ? escapedValue.split(/\r?\n/)
    : key === "company_name"
      ? escapedValue.replace(COMPANY_NAME_STACK_CONNECTORS, "\n$1").split("\n")
      : null;
  if (lines === null) return escapedValue;
  if (lines.length <= 1) return escapedValue;
  const stacked = lines.map(capitalizeLineStart).join("<br />");
  // Wrap a multi-line company_name in the data-company-stack sentinel so the
  // post-substitution reflowCompanyStatus() pass can lift a trailing status
  // suffix — "(In Liquidation)" etc., whether a {{company_status}} field or
  // hand-typed — onto the registered-name (first) line. Only company_name is
  // marked; other stacked fields render the bare <br>-joined value (MOR-634).
  return key === "company_name"
    ? `<span data-company-stack>${stacked}</span>`
    : stacked;
}

/**
 * Canonical name-list fields whose value is a `\n`-joined set of names
 * (`fieldMapping.ts` builds them with `names.join("\n")`). Rendered inline in a
 * sentence — e.g. `Formerly trading as "{{trading_name_lines}}"` — the raw
 * newlines collapse to spaces and the names run together with no separators.
 * For these keys the default (un-`stack`ed) render joins the names into a
 * grammatical list instead ("A, B, C and D"). The `stack` modifier still wins
 * when the author wants one name per line as a block. (MOR-777)
 */
export const NAME_LIST_FIELDS = new Set([
  "trading_name_lines",
  "former_trading_name_lines",
  "trust_name_lines",
]);

/**
 * Join a `\n`-separated (already HTML-escaped) value into a grammatical list:
 * one name unchanged, two joined with " and ", three or more comma-separated
 * with a final " and " (no Oxford comma, per MOR-777). Newlines aren't escaped,
 * so splitting the escaped value on them is safe. A single line (or an empty
 * value) is returned untouched.
 */
export function formatInlineNameList(escapedValue: string): string {
  const items = escapedValue
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length <= 1) return items[0] ?? escapedValue;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const COMPANY_STACK_OPEN = "<span data-company-stack>";
const COMPANY_STACK_CLOSE = "</span>";
// Inline formatting tags that may wrap the marker (bold/italic/etc.) or appear
// inside the status suffix. We track these so the wrapper stays spanning ALL
// stacked lines instead of closing after line 1 (MOR-634 bold-loss fix).
const INLINE_OPEN_RE = /^<(?:strong|b|em|i|u|s|span)(?:\s[^>]*)?>/i;
const INLINE_CLOSE_RE = /^<\/(?:strong|b|em|i|u|s|span)>/i;
// A block/line boundary halts the status scan — the suffix never crosses a line
// break or paragraph/list/table/heading edge.
const BLOCK_BOUNDARY_RE =
  /^<(?:br|\/?p|\/?div|\/?li|\/?ul|\/?ol|\/?tr|\/?td|\/?th|\/?table|\/?h[1-6])[\s/>]/i;
const FIRST_BR_RE = /<br\s*\/?>/i;

/**
 * Lift a company status suffix onto the registered-name line.
 *
 * `{{company_name|stack}}` renders the registered name / "trading as" / "also
 * known as" parts on separate lines, but a status suffix that follows it in the
 * template (`({{company_status}})` or hand-typed text) naturally lands after the
 * LAST line. Practitioners need the status attached to the legal name on the
 * FIRST line:
 *
 *     Ghoson Salwan Pty Ltd (In Liquidation)
 *     Trading as "Golden Cherry"
 *     ACN 627 400 935 (the Company)
 *
 * The company name is often wrapped in inline formatting (`<strong>` etc.). The
 * scan is therefore tag-aware so the wrapper keeps spanning every stacked line:
 *   - `<strong>{marker}</strong> (status)` — the wrapper closes BEFORE the
 *     suffix: its closing tags are moved to after the lifted suffix + rest.
 *   - `<strong>{marker} (status)</strong>` — the wrapper closes AFTER the
 *     suffix: the scan stops at that unbalanced close, leaving it in place to
 *     enclose every line.
 *
 * Runs once on fully-substituted HTML (the marker is added by applyStackLayout).
 * Idempotent: with no marker the input is returned unchanged; the marker is
 * always unwrapped so it never reaches Word/PDF export. (MOR-634)
 */
export function reflowCompanyStatus(html: string): string {
  let out = "";
  let cursor = 0;
  for (;;) {
    const start = html.indexOf(COMPANY_STACK_OPEN, cursor);
    if (start === -1) {
      out += html.slice(cursor);
      return out;
    }
    out += html.slice(cursor, start);
    const innerStart = start + COMPANY_STACK_OPEN.length;
    const innerEnd = html.indexOf(COMPANY_STACK_CLOSE, innerStart);
    if (innerEnd === -1) {
      // Malformed marker — emit the remainder untouched and stop.
      out += html.slice(start);
      return out;
    }
    const inner = html.slice(innerStart, innerEnd);
    let pos = innerEnd + COMPANY_STACK_CLOSE.length;

    // 1. Wrapper closing tags sitting immediately after the marker (the wrapper
    //    closed before the suffix). They must move to after the lifted suffix so
    //    the formatting still encloses every stacked line.
    let leadClose = "";
    for (let m = INLINE_CLOSE_RE.exec(html.slice(pos)); m;) {
      leadClose += m[0];
      pos += m[0].length;
      m = INLINE_CLOSE_RE.exec(html.slice(pos));
    }

    // 2. Scan the status suffix, tracking inline-tag depth. Stop at a block/line
    //    boundary, or at a closing tag that is unbalanced at depth 0 (a wrapper
    //    that encloses the suffix — leave it in place to close after all lines).
    let trailing = "";
    let depth = 0;
    while (pos < html.length) {
      const rest = html.slice(pos);
      if (depth === 0 && BLOCK_BOUNDARY_RE.test(rest)) break;
      const open = INLINE_OPEN_RE.exec(rest);
      if (open) {
        depth += 1;
        trailing += open[0];
        pos += open[0].length;
        continue;
      }
      const close = INLINE_CLOSE_RE.exec(rest);
      if (close) {
        if (depth === 0) break;
        depth -= 1;
        trailing += close[0];
        pos += close[0].length;
        continue;
      }
      trailing += rest[0];
      pos += 1;
    }

    const brIdx = inner.search(FIRST_BR_RE);
    if (trailing.trim() === "" || brIdx === -1) {
      // Nothing to lift (or a degenerate single-line marker): just unwrap.
      out += inner + leadClose + trailing;
    } else {
      out += inner.slice(0, brIdx) + trailing + inner.slice(brIdx) + leadClose;
    }
    cursor = pos;
  }
}

/**
 * Apply modifiers to a PLAIN-TEXT value (before any HTML escaping).
 *
 * Returns "" when the value is empty/whitespace so an empty field never renders
 * a bare `()` / `[]` / `""` / `ABN`. Apply order is fixed regardless of the
 * stored order: dateformat → case → plural/possessive → wrap → prefix.
 */
export function applyFieldModifiers(
  value: string,
  mods: FieldModifier[],
): string {
  if (!value || value.trim() === "") return value;
  if (!mods.length) return value;

  // Phase 0: date format (at most one) — reshapes the raw ISO value before any
  // textual decoration. A no-op on non-ISO values.
  const dateMod = mods.find((m) => MODIFIER_CATALOG[m].group === "dateformat");
  const afterDate = dateMod ? MODIFIER_CATALOG[dateMod].apply(value) : value;
  // Phase 1: case (at most one).
  const caseMod = mods.find((m) => MODIFIER_CATALOG[m].group === "case");
  const afterCase = caseMod
    ? MODIFIER_CATALOG[caseMod].apply(afterDate)
    : afterDate;
  // Phase 2: grammatical transforms (order: plural then possessive).
  const afterTransforms = TRANSFORM_MODIFIERS.reduce(
    (acc, m) => (mods.includes(m) ? MODIFIER_CATALOG[m].apply(acc) : acc),
    afterCase,
  );
  // Phase 3: wrap (at most one).
  const wrapMod = mods.find((m) => MODIFIER_CATALOG[m].group === "wrap");
  const afterWrap = wrapMod
    ? MODIFIER_CATALOG[wrapMod].apply(afterTransforms)
    : afterTransforms;
  // Phase 4: prefix label (at most one) — applied last so the label sits
  // outermost, e.g. `ABN {value}`.
  const prefixMod = mods.find((m) => MODIFIER_CATALOG[m].group === "prefix");
  return prefixMod ? MODIFIER_CATALOG[prefixMod].apply(afterWrap) : afterWrap;
}

/**
 * Parse a raw mods string (`"parens|plural"`, optionally with a leading `|`)
 * into a validated, de-duplicated modifier list. Unknown tokens are dropped;
 * at most one wrap and at most one case modifier are kept (last occurrence
 * wins), so the list is always renderable.
 */
export function parseModifiers(
  raw: string | null | undefined,
): FieldModifier[] {
  if (!raw) return [];
  const parts = raw
    .split("|")
    .map((p) => p.trim())
    .filter((p): p is FieldModifier => p.length > 0 && isFieldModifier(p));

  const { dateMod, wrap, caseMod, prefix, layout, transforms } = parts.reduce(
    (acc, m) => {
      const group = MODIFIER_CATALOG[m].group;
      if (group === "dateformat") acc.dateMod = m;
      else if (group === "wrap") acc.wrap = m;
      else if (group === "case") acc.caseMod = m;
      else if (group === "prefix") acc.prefix = m;
      else if (group === "layout") acc.layout = m;
      else acc.transforms.add(m);
      return acc;
    },
    {
      dateMod: null as FieldModifier | null,
      wrap: null as FieldModifier | null,
      caseMod: null as FieldModifier | null,
      prefix: null as FieldModifier | null,
      layout: null as FieldModifier | null,
      transforms: new Set<FieldModifier>(),
    },
  );

  // Canonical order: dateformat, transforms (plural, possessive), case, wrap,
  // prefix, layout. Layout goes last so existing stored tokens stay
  // byte-stable; prefix slots in before it (no prior tokens used prefix).
  const result: FieldModifier[] = [];
  if (dateMod) result.push(dateMod);
  for (const m of TRANSFORM_MODIFIERS) if (transforms.has(m)) result.push(m);
  if (caseMod) result.push(caseMod);
  if (wrap) result.push(wrap);
  if (prefix) result.push(prefix);
  if (layout) result.push(layout);
  return result;
}

/** Serialize a modifier list to the canonical `"plural|parens"` form. */
export function serializeModifiers(mods: FieldModifier[]): string {
  // Re-run through parseModifiers to enforce canonical order + constraints.
  return parseModifiers(mods.join("|")).join("|");
}

const WRAP_CHARS: Record<string, [string, string]> = {
  parens: ["(", ")"],
  brackets: ["[", "]"],
  quotes: ['"', '"'],
};

const TRANSFORM_BADGES: Record<string, string> = {
  plural: "+s",
  possessive: "'s",
  upper: "ABC",
  lower: "abc",
  capitalize: "Ab",
  date_dmy_abbr: "D Mon Y",
  date_dmy_slash: "DD/MM",
  date_dmy_dash: "DD-MM",
  date_dmy_dot: "DD.MM",
  date_iso: "ISO",
  date_dmy_ordinal: "Do",
  date_weekday: "Wkdy",
  stack: "⏎",
};

/** Leading label text a prefix modifier prepends to the chip's displayed value. */
const PREFIX_LABELS: Record<string, string> = {
  abn_label: "ABN ",
  acn_label: "ACN ",
};

/**
 * Visual decoration parts for a chip label, used by both the editor NodeView
 * (DOM) and the static preview/placeholder renderers (HTML strings) so they
 * stay in lockstep. `open`/`close` hug the label (wrap modifiers); `badges`
 * are short muted tags shown after it (grammatical / case modifiers).
 */
export function modifierDecoration(mods: FieldModifier[]): {
  open: string;
  close: string;
  badges: string[];
} {
  const wrap = mods.find((m) => MODIFIER_CATALOG[m].group === "wrap");
  const [wrapOpen, close] = wrap ? WRAP_CHARS[wrap] : ["", ""];
  // A prefix label hugs the start of the value (like a wrap's open char), so
  // the chip reads e.g. `ABN {value}` — render it in `open`, not as a badge.
  const prefix = mods.find((m) => MODIFIER_CATALOG[m].group === "prefix");
  const open = `${prefix ? PREFIX_LABELS[prefix] : ""}${wrapOpen}`;
  const badges = mods
    .filter((m) => {
      const group = MODIFIER_CATALOG[m].group;
      return group !== "wrap" && group !== "prefix";
    })
    .map((m) => TRANSFORM_BADGES[m] ?? m);
  return { open, close, badges };
}
